import type { InboxDetail } from '@/api/lcCrm'
import { FRANCHISE_TYPE_LABELS, INTEREST_LABELS } from '../support/tabs/inquiryLabels'
import { acquisitionChannelLabel } from './acquisitionChannel'
import { INTAKE_ATTENDANCE_LABEL, SESSION_MODE_LABEL, formatPhone, labelOf } from './crmLabels'

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

export function applicationLabels(value: unknown, labels: Record<string, string>): string {
  if (!Array.isArray(value)) return '-'
  return value.filter((item): item is string => typeof item === 'string')
    .map((item) => labels[item] ?? item).join(', ') || '-'
}

export function consentLabel(value: unknown, consentedAt?: unknown): string {
  if (typeof value === 'boolean') return value ? '동의' : '미동의'
  return text(consentedAt) ? '동의' : '기록 없음'
}

/** Business fields are explicit: import metadata and internal identifiers never become application content. */
export function IntakeApplicationDetails({ intake }: { intake: InboxDetail }) {
  const payload = intake.sourcePayload
  const fields: [string, string][] = [
    ['신청자', text(payload.name) ?? intake.contactName ?? '-'],
    ['전화번호', formatPhone(text(payload.phone) ?? intake.phoneNormalized)],
    ['유입경로', acquisitionChannelLabel(intake.acquisitionChannel)],
    ['가맹 유형', applicationLabels(payload.franchiseTypes, FRANCHISE_TYPE_LABELS)],
    ['지역', [text(payload.sido) ?? intake.sido, text(payload.sigungu) ?? intake.sigungu].filter(Boolean).join(' ') || '-'],
  ]
  if (intake.sourceType === 'BRIEFING') {
    fields.push(
      ['관심 항목', applicationLabels(payload.interests, INTEREST_LABELS)],
      ['기타 직접 입력', text(payload.otherText) ?? '-'],
      ['회차·모드', [intake.sessionDate, intake.sessionMode ? labelOf(SESSION_MODE_LABEL, intake.sessionMode) : undefined].filter(Boolean).join(' · ') || '-'],
      ['참석 결과', labelOf(INTAKE_ATTENDANCE_LABEL, intake.attendanceStatus)],
    )
  }
  if (intake.sourceType === 'FRANCHISE') fields.push(['문의 내용', text(payload.inquiryText) ?? '-'])
  fields.push(
    ['개인정보 동의', consentLabel(payload.privacyConsent, payload.privacyConsentedAt)],
    ['마케팅 동의', consentLabel(payload.marketingConsent)],
  )
  if (text(payload.notes)) fields.push(['비고', text(payload.notes)!])
  return (
    <dl className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {fields.map(([label, value]) => (
        <div key={label} className="flex min-w-0 flex-col gap-1">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
